
import React, {
    useEffect,
    useState
} from "react";

import {
    Link,
    useNavigate,
    useParams
} from "react-router-dom";

import Swal from "sweetalert2";

import API_BASE_URL from "../config/api";
import QuillEditor from "../components/QuillEditor";


function EditQuestion() {

    const { id } = useParams();

    const navigate = useNavigate();


    /*
    |--------------------------------------------------------------------------
    | API URLs
    |--------------------------------------------------------------------------
    */

    const API_URL =
        `${API_BASE_URL}/admin/questions/${encodeURIComponent(id)}`;

    const CATEGORY_API_URL =
        `${API_BASE_URL}/admin/question-paper-categories`;

    const PAPERS_API_URL =
        `${API_BASE_URL}/admin/question-papers`;


    /*
    |--------------------------------------------------------------------------
    | State
    |--------------------------------------------------------------------------
    */

    const [categories, setCategories] =
        useState([]);

    const [papers, setPapers] =
        useState([]);

    const [selectedCategory, setSelectedCategory] =
        useState("");

    const [loading, setLoading] =
        useState(true);

    const [loadingCategories, setLoadingCategories] =
        useState(true);

    const [loadingPapers, setLoadingPapers] =
        useState(false);

    const [saving, setSaving] =
        useState(false);

    const [error, setError] =
        useState("");


    /*
    |--------------------------------------------------------------------------
    | Form Data
    |--------------------------------------------------------------------------
    */

    const [formData, setFormData] = useState({

        Q_number: "",

        paper: [],

        question: "",

        sort_order: "",

        option1: "",

        option2: "",

        option3: "",

        option4: "",

        right_option: "",

        explanation: ""

    });


    /*
    |--------------------------------------------------------------------------
    | Fetch Categories
    |--------------------------------------------------------------------------
    */

    const fetchCategories = async () => {

        try {

            setLoadingCategories(true);

            setError("");


            const response =
                await fetch(
                    CATEGORY_API_URL,
                    {
                        method: "GET",

                        headers: {
                            Accept:
                                "application/json"
                        },

                        credentials: "include"
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | Unauthorized
            |--------------------------------------------------------------------------
            */

            if (response.status === 401) {

                throw new Error(
                    "Admin authentication failed. Please login again."
                );

            }


            /*
            |--------------------------------------------------------------------------
            | Server Error
            |--------------------------------------------------------------------------
            */

            if (!response.ok) {

                throw new Error(
                    `Server Error: ${response.status}`
                );

            }


            const data =
                await response.json();


            if (!data.success) {

                throw new Error(
                    data.message ||
                    "Unable to fetch paper categories."
                );

            }


            const categoryList =
    Array.isArray(data.data)
        ? data.data
        : [];


            setCategories(
                categoryList
            );


            return categoryList;


        } catch (error) {

            console.error(
                "Category API Error:",
                error
            );

            throw error;

        } finally {

            setLoadingCategories(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Fetch Papers By Category
    |--------------------------------------------------------------------------
    */

    const fetchPapersByCategory = async (
        category
    ) => {

        if (!category) {

            setPapers([]);

            return [];

        }


        try {

            setLoadingPapers(true);

            setError("");


            const response =
                await fetch(
                    `${PAPERS_API_URL}/${encodeURIComponent(category)}`,
                    {
                        method: "GET",

                        headers: {
                            Accept:
                                "application/json"
                        },

                        credentials: "include"
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | Unauthorized
            |--------------------------------------------------------------------------
            */

            if (response.status === 401) {

                throw new Error(
                    "Admin authentication failed. Please login again."
                );

            }


            /*
            |--------------------------------------------------------------------------
            | Server Error
            |--------------------------------------------------------------------------
            */

            if (!response.ok) {

                throw new Error(
                    `Server Error: ${response.status}`
                );

            }


            const data =
                await response.json();


            if (!data.success) {

                throw new Error(
                    data.message ||
                    "Unable to fetch papers."
                );

            }


            const paperList =
                Array.isArray(data.papers)
                    ? data.papers
                    : [];


            setPapers(
                paperList
            );


            return paperList;


        } catch (error) {

            console.error(
                "Papers API Error:",
                error
            );

            throw error;

        } finally {

            setLoadingPapers(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Fetch Existing Question
    |--------------------------------------------------------------------------
    */

    const fetchQuestion = async () => {

        try {

            setLoading(true);

            setError("");


            /*
            |--------------------------------------------------------------------------
            | Fetch Categories First
            |--------------------------------------------------------------------------
            */

            await fetchCategories();


            /*
            |--------------------------------------------------------------------------
            | Fetch Existing Question
            |--------------------------------------------------------------------------
            */

            const response =
                await fetch(
                    API_URL,
                    {
                        method: "GET",

                        headers: {
                            Accept:
                                "application/json"
                        },

                        credentials: "include"
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | Unauthorized
            |--------------------------------------------------------------------------
            */

            if (response.status === 401) {

                throw new Error(
                    "Admin authentication failed. Please login again."
                );

            }


            /*
            |--------------------------------------------------------------------------
            | Server Error
            |--------------------------------------------------------------------------
            */

            if (!response.ok) {

                throw new Error(
                    `Server Error: ${response.status}`
                );

            }


            const data =
                await response.json();


            console.log(
                "Question API Response:",
                data
            );


            if (!data.success) {

                throw new Error(
                    data.message ||
                    "Question not found."
                );

            }


            const question =
                data.question;


            /*
            |--------------------------------------------------------------------------
            | Existing Paper IDs
            |--------------------------------------------------------------------------
            */

            let selectedPapers = [];


            if (
                Array.isArray(
                    question.paper
                )
            ) {

                selectedPapers =
                    question.paper.map(
                        value =>
                            String(value)
                    );

            } else if (
                question.paper_id !==
                undefined &&
                question.paper_id !== null
            ) {

                selectedPapers = [

                    String(
                        question.paper_id
                    )

                ];

            }


            /*
            |--------------------------------------------------------------------------
            | Find Existing Category
            |--------------------------------------------------------------------------
            */

            let existingCategory = "";


            if (
                question.category
            ) {

                existingCategory =
                    question.category;

            } else if (
                question.paper_category
            ) {

                existingCategory =
                    question.paper_category;

            }


            /*
            |--------------------------------------------------------------------------
            | Fallback: Find Category From Paper
            |--------------------------------------------------------------------------
            */

            if (
                !existingCategory &&
                question.paper_id
            ) {

                try {

                    const selectedPaperResponse =
                        await fetch(
                            `${API_BASE_URL}/admin/papers/${encodeURIComponent(question.paper_id)}`,
                            {
                                method: "GET",

                                headers: {
                                    Accept:
                                        "application/json"
                                },

                                credentials: "include"
                            }
                        );


                    if (
                        selectedPaperResponse.ok
                    ) {

                        const paperData =
                            await selectedPaperResponse.json();


                        if (
                            paperData.success &&
                            paperData.paper
                        ) {

                            existingCategory =
                                paperData.paper.category;

                        }

                    }

                } catch (paperError) {

                    console.error(
                        "Paper Details Error:",
                        paperError
                    );

                }

            }


            /*
            |--------------------------------------------------------------------------
            | Set Selected Category
            |--------------------------------------------------------------------------
            */

            setSelectedCategory(
                existingCategory || ""
            );


            /*
            |--------------------------------------------------------------------------
            | Fetch Papers For Existing Category
            |--------------------------------------------------------------------------
            */

            let categoryPapers = [];


            if (
                existingCategory
            ) {

                categoryPapers =
                    await fetchPapersByCategory(
                        existingCategory
                    );

            }


            /*
            |--------------------------------------------------------------------------
            | Set Papers
            |--------------------------------------------------------------------------
            */

            setPapers(
                categoryPapers
            );


            /*
            |--------------------------------------------------------------------------
            | Set Form Data
            |--------------------------------------------------------------------------
            */

            setFormData({

                Q_number:
                    question.Q_number ??
                    "",

                paper:
                    selectedPapers,

                question:
                    question.question ||
                    "",

                sort_order:
                    question.sort_order ??
                    "",

                option1:
                    question.option1 ||
                    "",

                option2:
                    question.option2 ||
                    "",

                option3:
                    question.option3 ||
                    "",

                option4:
                    question.option4 ||
                    "",

                right_option:
                    question.right_option ||
                    "",

                explanation:
                    question.explanation ||
                    ""

            });


        } catch (error) {

            console.error(
                "Edit Question Error:",
                error
            );


            setError(
                error.message ||
                "Unable to fetch question."
            );

        } finally {

            setLoading(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Load Question On Page Load
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        fetchQuestion();

    }, [id]);


    /*
    |--------------------------------------------------------------------------
    | Handle Normal Input
    |--------------------------------------------------------------------------
    */

    const handleChange = (
        event
    ) => {

        const {
            name,
            value
        } = event.target;


        setFormData(
            previous => ({

                ...previous,

                [name]:
                    value

            })
        );

    };


    /*
    |--------------------------------------------------------------------------
    | Handle Category Change
    |--------------------------------------------------------------------------
    */

    const handleCategoryChange = async (
        event
    ) => {

        const category =
            event.target.value;


        setSelectedCategory(
            category
        );


        /*
        |--------------------------------------------------------------------------
        | Clear Existing Paper Selection
        |--------------------------------------------------------------------------
        */

        setFormData(
            previous => ({

                ...previous,

                paper: []

            })
        );


        /*
        |--------------------------------------------------------------------------
        | Fetch Papers
        |--------------------------------------------------------------------------
        */

        try {

            await fetchPapersByCategory(
                category
            );

        } catch (error) {

            console.error(
                "Category Change Error:",
                error
            );


            setError(
                error.message ||
                "Unable to fetch papers."
            );

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Handle Paper Selection
    |--------------------------------------------------------------------------
    */

    const handlePaperChange = (
        event
    ) => {

        const selectedOptions =
            Array.from(
                event.target.selectedOptions
            );


        const selectedPaperIds =
            selectedOptions.map(
                option =>
                    option.value
            );


        setFormData(
            previous => ({

                ...previous,

                paper:
                    selectedPaperIds

            })
        );

    };


    /*
    |--------------------------------------------------------------------------
    | Submit Update
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async (
        event
    ) => {

        event.preventDefault();


        /*
        |--------------------------------------------------------------------------
        | Validate Paper
        |--------------------------------------------------------------------------
        */

        if (
            !formData.paper ||
            formData.paper.length === 0
        ) {

            Swal.fire(
                "Validation Error",
                "Please select at least one paper.",
                "warning"
            );

            return;

        }


        /*
        |--------------------------------------------------------------------------
        | Validate Question
        |--------------------------------------------------------------------------
        */

        if (
            !formData.question ||
            !formData.question.trim()
        ) {

            Swal.fire(
                "Validation Error",
                "Please enter question.",
                "warning"
            );

            return;

        }


        /*
        |--------------------------------------------------------------------------
        | Validate Options
        |--------------------------------------------------------------------------
        */

        if (
            !formData.option1.trim() ||
            !formData.option2.trim() ||
            !formData.option3.trim() ||
            !formData.option4.trim()
        ) {

            Swal.fire(
                "Validation Error",
                "Please enter all four options.",
                "warning"
            );

            return;

        }


        /*
        |--------------------------------------------------------------------------
        | Validate Right Option
        |--------------------------------------------------------------------------
        */

        if (
            !formData.right_option.trim()
        ) {

            Swal.fire(
                "Validation Error",
                "Please select right option.",
                "warning"
            );

            return;

        }


        try {

            setSaving(true);

            setError("");


            /*
            |--------------------------------------------------------------------------
            | PUT Request
            |--------------------------------------------------------------------------
            */

            const response =
                await fetch(
                    API_URL,
                    {
                        method: "PUT",

                        headers: {

                            "Content-Type":
                                "application/json",

                            Accept:
                                "application/json"

                        },

                        credentials:
                            "include",

                        body:
                            JSON.stringify({

                                paper:
                                    formData.paper,

                                question:
                                    formData.question,

                                sort_order:
                                    formData.sort_order,

                                option1:
                                    formData.option1,

                                option2:
                                    formData.option2,

                                option3:
                                    formData.option3,

                                option4:
                                    formData.option4,

                                right_option:
                                    formData.right_option,

                                explanation:
                                    formData.explanation

                            })

                    }
                );


            /*
            |--------------------------------------------------------------------------
            | Unauthorized
            |--------------------------------------------------------------------------
            */

            if (
                response.status === 401
            ) {

                throw new Error(
                    "Admin authentication failed. Please login again."
                );

            }


            /*
            |--------------------------------------------------------------------------
            | Read JSON
            |--------------------------------------------------------------------------
            */

            const data =
                await response.json();


            console.log(
                "Update Question Response:",
                data
            );


            /*
            |--------------------------------------------------------------------------
            | Server Error
            |--------------------------------------------------------------------------
            */

            if (!response.ok) {

                throw new Error(
                    data.message ||
                    `Server Error: ${response.status}`
                );

            }


            /*
            |--------------------------------------------------------------------------
            | Success
            |--------------------------------------------------------------------------
            */

            if (data.success) {

    await Swal.fire({

        icon: "success",

        title: "Question Updated",

        text:
            data.message ||
            "Question updated successfully.",

        confirmButtonText: "OK"

    });

} else {

                throw new Error(
                    data.message ||
                    "Unable to update question."
                );

            }


        } catch (error) {

            console.error(
                "Update Question Error:",
                error
            );


            setError(
                error.message ||
                "Unable to connect with server."
            );


            Swal.fire(
                "Error",
                error.message ||
                "Unable to update question.",
                "error"
            );


        } finally {

            setSaving(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Loading Screen
    |--------------------------------------------------------------------------
    */

    if (loading) {

        return (

            <div
                className="
                    container-fluid
                    d-flex
                    justify-content-center
                    align-items-center
                "
                style={{
                    minHeight:
                        "400px"
                }}
            >

                <div
                    className="text-center"
                >

                    <div
                        className="
                            spinner-border
                            text-primary
                        "
                        role="status"
                    >
                    </div>


                    <div
                        className="
                            mt-3
                            text-muted
                        "
                    >
                        Loading question...
                    </div>

                </div>

            </div>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | Main UI
    |--------------------------------------------------------------------------
    */

    return (

        <div
            className="container-fluid"
            style={{
                maxHeight:
                    "85vh",

                overflowY:
                    "auto"
            }}
        >


            {/* =========================================================
                PAGE HEADER
            ========================================================= */}

            <div
                className="
                    d-flex
                    justify-content-between
                    align-items-center
                    mb-4
                "
            >

                <div>

                    <h3
                        className="mb-1"
                    >
                        Edit Question
                    </h3>


                    <p
                        className="
                            text-muted
                            mb-0
                        "
                    >

                        Question ID:

                        <strong
                            className="ms-2"
                        >
                            {id}
                        </strong>


                        {formData.Q_number && (

                            <>

                                <span
                                    className="ms-3"
                                >
                                    Q Number:
                                </span>


                                <strong
                                    className="ms-2"
                                >
                                    {formData.Q_number}
                                </strong>

                            </>

                        )}

                    </p>

                </div>


                <Link
                    to="/questions"
                    className="
                        btn
                        btn-outline-primary
                    "
                >

                    <i
                        className="
                            fa
                            fa-arrow-left
                            me-2
                        "
                    ></i>

                    Back

                </Link>

            </div>


            {/* =========================================================
                ERROR
            ========================================================= */}

            {error && (

                <div
                    className="
                        alert
                        alert-danger
                    "
                >

                    {error}

                </div>

            )}


            {/* =========================================================
                CARD
            ========================================================= */}

            <div
                className="
                    card
                    shadow-sm
                "
            >

                <div
                    className="
                        card-header
                        bg-white
                    "
                >

                    <h5
                        className="mb-0"
                    >
                        Question Details
                    </h5>

                </div>


                <div
                    className="card-body"
                >

                    <form
                        onSubmit={
                            handleSubmit
                        }
                    >


                        {/* =================================================
                            PAPER CATEGORY
                        ================================================= */}

                        <div
                            className="mb-4"
                        >

                            <label
                                className="
                                    form-label
                                    fw-bold
                                "
                            >

                                Select Paper Category

                                <span
                                    className="
                                        text-danger
                                    "
                                >
                                    {" "}*
                                </span>

                            </label>


                            <select
                                className="form-select"
                                value={
                                    selectedCategory
                                }
                                onChange={
                                    handleCategoryChange
                                }
                                disabled={
                                    loadingCategories
                                }
                            >

                                <option value="">
                                    ----- Select Paper Category -----
                                </option>


                                {categories.map(
                                    category => (

                                        <option
                                            key={
                                                category.id
                                            }
                                            value={
                                                category.paper
                                            }
                                        >

                                            {
                                                category.paper
                                            }

                                        </option>

                                    )
                                )}

                            </select>


                            {loadingCategories && (

                                <small
                                    className="
                                        text-muted
                                    "
                                >
                                    Loading categories...
                                </small>

                            )}

                        </div>


                        {/* =================================================
                            PAPERS
                        ================================================= */}

                        <div
                            className="mb-4"
                        >

                            <label
                                className="
                                    form-label
                                    fw-bold
                                "
                            >

                                Select Paper

                                <span
                                    className="
                                        text-danger
                                    "
                                >
                                    {" "}*
                                </span>

                            </label>


                            <select
                                name="paper"
                                className="form-select"
                                multiple
                                size="6"
                                value={
                                    formData.paper
                                }
                                onChange={
                                    handlePaperChange
                                }
                                disabled={
                                    !selectedCategory ||
                                    loadingPapers
                                }
                            >

                                {!selectedCategory ? (

                                    <option disabled>
                                        ----- Select Category First -----
                                    </option>

                                ) : loadingPapers ? (

                                    <option disabled>
                                        Loading papers...
                                    </option>

                                ) : papers.length > 0 ? (

                                    papers.map(
                                        paper => (

                                            <option
                                                key={
                                                    paper.paper_id
                                                }
                                                value={
                                                    String(
                                                        paper.paper_id
                                                    )
                                                }
                                            >

                                                {
                                                    paper.paper
                                                }

                                                {" - "}

                                                {
                                                    paper.paper_type
                                                }

                                                {" - "}

                                                {
                                                    paper.category
                                                }

                                            </option>

                                        )
                                    )

                                ) : (

                                    <option disabled>
                                        No papers found.
                                    </option>

                                )}

                            </select>


                            <small
                                className="
                                    text-muted
                                "
                            >

                                Hold Ctrl to select
                                multiple papers.

                            </small>

                        </div>


                        {/* =================================================
                            Q NUMBER
                        ================================================= */}

                        <div
                            className="mb-4"
                        >

                            <label
                                className="
                                    form-label
                                    fw-bold
                                "
                            >
                                Question Number
                            </label>


                            <input
                                type="number"
                                className="form-control"
                                value={
                                    formData.Q_number
                                }
                                disabled
                            />


                            <small
                                className="
                                    text-muted
                                "
                            >
                                Question number cannot be changed.
                            </small>

                        </div>


                        {/* =================================================
                            QUESTION
                        ================================================= */}

                        
                        <div className="mb-4">
                            <label className="form-label fw-bold">
                                Question <span className="text-danger">*</span>
                            </label>

                            <QuillEditor
                                value={formData.question || ""}
                                onChange={(value) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        question: value,
                                    }))
                                }
                                placeholder="Enter question..."
                            />
                        </div>



                        {/* =================================================
                            SORT ORDER
                        ================================================= */}

                        <div
                            className="mb-4"
                        >

                            <label
                                className="
                                    form-label
                                    fw-bold
                                "
                            >
                                Sort Order
                            </label>


                            <input
                                type="number"
                                name="sort_order"
                                className="form-control"
                                placeholder="Enter sort order"
                                value={
                                    formData.sort_order
                                }
                                onChange={
                                    handleChange
                                }
                            />

                        </div>


                        {/* =================================================
                            OPTIONS
                        ================================================= */}

                        <div
                            className="row"
                        >


                            {/* OPTION 1 */}

                            <div
                                className="
                                    col-md-6
                                    mb-3
                                "
                            >

                                <label
                                    className="
                                        form-label
                                        fw-bold
                                    "
                                >
                                    Option 1
                                </label>


                                <input
                                    type="text"
                                    name="option1"
                                    className="form-control"
                                    maxLength="80"
                                    value={
                                        formData.option1
                                    }
                                    onChange={
                                        handleChange
                                    }
                                />

                            </div>


                            {/* OPTION 2 */}

                            <div
                                className="
                                    col-md-6
                                    mb-3
                                "
                            >

                                <label
                                    className="
                                        form-label
                                        fw-bold
                                    "
                                >
                                    Option 2
                                </label>


                                <input
                                    type="text"
                                    name="option2"
                                    className="form-control"
                                    maxLength="80"
                                    value={
                                        formData.option2
                                    }
                                    onChange={
                                        handleChange
                                    }
                                />

                            </div>


                            {/* OPTION 3 */}

                            <div
                                className="
                                    col-md-6
                                    mb-3
                                "
                            >

                                <label
                                    className="
                                        form-label
                                        fw-bold
                                    "
                                >
                                    Option 3
                                </label>


                                <input
                                    type="text"
                                    name="option3"
                                    className="form-control"
                                    maxLength="80"
                                    value={
                                        formData.option3
                                    }
                                    onChange={
                                        handleChange
                                    }
                                />

                            </div>


                            {/* OPTION 4 */}

                            <div
                                className="
                                    col-md-6
                                    mb-3
                                "
                            >

                                <label
                                    className="
                                        form-label
                                        fw-bold
                                    "
                                >
                                    Option 4
                                </label>


                                <input
                                    type="text"
                                    name="option4"
                                    className="form-control"
                                    maxLength="80"
                                    value={
                                        formData.option4
                                    }
                                    onChange={
                                        handleChange
                                    }
                                />

                            </div>

                        </div>


                        {/* =================================================
                            RIGHT OPTION
                        ================================================= */}

                        <div className="mb-4">

                            <label className="form-label fw-bold">
                                Right Option

                                <span className="text-danger">
                                    {" "}*
                                </span>
                            </label>

                            <select
                                name="right_option"
                                className="form-select"
                                value={formData.right_option}
                                onChange={handleChange}
                                required
                            >

                                <option value="">
                                    ----- Select Right Option -----
                                </option>

                                <option value="1">
                                    Option 1
                                </option>

                                <option value="2">
                                    Option 2
                                </option>

                                <option value="3">
                                    Option 3
                                </option>

                                <option value="4">
                                    Option 4
                                </option>

                            </select>

                        </div>


                        {/* =================================================
                            EXPLANATION
                        ================================================= */}

                        <div
                            className="mb-4"
                        >

                            <label
                                className="
                                    form-label
                                    fw-bold
                                "
                            >
                                Explanation
                            </label>


                            <QuillEditor
    value={formData.explanation}
    onChange={(value) =>
        setFormData(prev => ({
            ...prev,
            explanation: value
        }))
    }
    placeholder="Enter explanation..."
/>

                        </div>


                        {/* =================================================
                            BUTTONS
                        ================================================= */}

                        <div
                            className="
                                d-flex
                                gap-2
                            "
                        >

                            <button
                                type="submit"
                                className="
                                    btn
                                    btn-primary
                                "
                                disabled={
                                    saving
                                }
                            >

                                {saving ? (

                                    <>

                                        <span
                                            className="
                                                spinner-border
                                                spinner-border-sm
                                                me-2
                                            "
                                        ></span>

                                        Updating...

                                    </>

                                ) : (

                                    <>

                                        <i
                                            className="
                                                fa
                                                fa-save
                                                me-2
                                            "
                                        ></i>

                                        Update Question

                                    </>

                                )}

                            </button>


                            <Link
                                to="/questions"
                                className="
                                    btn
                                    btn-secondary
                                "
                            >
                                Cancel
                            </Link>

                        </div>


                    </form>

                </div>

            </div>

        </div>

    );

}


export default EditQuestion;

